import React, { useState, useRef, useCallback, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatRelativeDate, formatDate, getStatusColor } from '@/lib/utils'
import { ArrowLeft, Mail, Phone, Building2, MapPin, Edit3, Check, X, Trash2, MessageSquare, FileText, TrendingUp, Link2, ChevronRight, Wrench, Send, Loader2, ShieldCheck, Clock, ShieldOff } from 'lucide-react'
import { toast } from 'sonner'
import Avatar from '@/components/Avatar'
import NoteThread from '@/components/NoteThread'
import TagEditor from '@/components/TagEditor'
import CustomFieldsEditor from '@/components/CustomFieldsEditor'
import TasksThread from '@/components/TasksThread'
import MeetingsThread from '@/components/MeetingsThread'
import CompanyPicker from '@/components/CompanyPicker'
import ActivityTimeline from '@/components/ActivityTimeline'
import BillingTab from '@/components/BillingTab'
import { fromAddressPrefix, formatAddress } from '@/lib/address'
import { getQuoteStatusConfig, type QuoteStatus } from '@/pages/payments/quote-status'
import { HOLD_STATUSES } from '@/pages/field/jobStatusTransition'
import { formatCurrency } from '@/lib/utils'

function EditableField({ label, value, onSave, type = 'text', placeholder }: any) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')

  const handleSave = () => { onSave(val); setEditing(false) }

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input autoFocus type={type} value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false) }}
          placeholder={placeholder}
          style={{ flex: 1, padding: '6px 10px', background: 'var(--field-bg)', border: '1px solid var(--focus-ring)', borderRadius: 7, fontSize: 13, color: 'var(--ink-primary)', outline: 'none' }} />
        <button onClick={handleSave} style={{ padding: '6px 8px', background: 'var(--accent)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--accent-ink)', display: 'flex' }}><Check style={{ width: 13, height: 13 }} /></button>
        <button onClick={() => setEditing(false)} style={{ padding: '6px 8px', background: 'var(--border-subtle)', border: 'none', borderRadius: 7, cursor: 'pointer', color: 'var(--ink-secondary)', display: 'flex' }}><X style={{ width: 13, height: 13 }} /></button>
      </div>
    )
  }

  return (
    <div onClick={() => { setVal(value || ''); setEditing(true) }}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 7, cursor: 'text', marginLeft: -8 }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <span style={{ fontSize: 13, color: value ? 'var(--ink-primary)' : 'var(--ink-tertiary)' }}>
        {value || `Add ${label.toLowerCase()}...`}
      </span>
      <Edit3 style={{ width: 11, height: 11, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
    </div>
  )
}

export default function ContactDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { workspace, profile } = useAuth()
  const isOwnerOrAdmin = !!profile?.is_owner || profile?.role === 'admin'
  const [searchParams, setSearchParams] = useSearchParams()
  // Persist active tab via ?tab=. Validate against the known set so a bogus
  // URL (?tab=banana) doesn't render an empty pane; fall back to timeline.
  const VALID_TABS = ['timeline', 'jobs', 'quotes', 'deals', 'invoices', 'billing', 'activity', 'notes', 'tasks', 'meetings'] as const
  const tabFromUrl = searchParams.get('tab')
  const initialTab = (VALID_TABS as readonly string[]).includes(tabFromUrl || '') ? tabFromUrl! : 'timeline'
  const [activeTab, setActiveTabState] = useState(initialTab)
  const setActiveTab = useCallback((next: string) => {
    setActiveTabState(next)
    const params = new URLSearchParams(searchParams)
    if (next === 'timeline') params.delete('tab') // keep the canonical URL clean for the default tab
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }, [searchParams, setSearchParams])
  const [linking, setLinking] = useState(false)
  // Hold statuses (on_customer_hold / on_vendor_hold / pending_parts /
  // awaiting_approval) collapse into the single 'on_hold' synthetic tab
  // rather than getting one tab each — keeps the filter strip readable
  // while still letting an operator see all paused work in one place.
  // 'on_hold' is NOT a real status value; the query branches on it.
  const [jobsFilter, setJobsFilter] = useState<
    | 'all'
    | 'scheduled'
    | 'en_route'
    | 'on_site'
    | 'complete'
    | 'cancelled'
    | 'on_customer_hold'
    | 'on_vendor_hold'
    | 'pending_parts'
    | 'awaiting_approval'
    | 'on_hold'
  >('all')
  const [jobsLimit, setJobsLimit] = useState(25)
  const [quotesFilter, setQuotesFilter] = useState<'all' | QuoteStatus>('all')
  const [quotesLimit, setQuotesLimit] = useState(25)

  const { data: contact, isLoading } = useQuery({
    queryKey: ['contact', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('contacts').select('*').eq('id', id).single()
      if (error) throw error
      return data
    },
    enabled: !!id,
  })

  const { data: conversations = [] } = useQuery({
    queryKey: ['contact-conversations', id],
    queryFn: async () => {
      const { data } = await supabase.from('conversations').select('*').eq('contact_id', id).order('created_at', { ascending: false }).limit(10)
      return data || []
    },
    enabled: !!id,
  })

  const { data: deals = [] } = useQuery({
    queryKey: ['contact-deals', id],
    queryFn: async () => {
      const { data } = await supabase.from('deals').select('*').eq('contact_id', id).order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!id,
  })

  const { data: invoices = [] } = useQuery({
    queryKey: ['contact-invoices', id],
    queryFn: async () => {
      const { data } = await supabase.from('invoices').select('*').eq('contact_id', id).order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!id,
  })

  const { data: jobs = [] } = useQuery({
    queryKey: ['contact-jobs', id, jobsFilter, jobsLimit],
    queryFn: async () => {
      let q = supabase
        .from('field_jobs')
        .select('id, scheduled_date, status, address_line1, address_line2, address_city, address_state, address_zip, address_country, total, assigned_agent_id, assigned_agent_name, job_number')
        .eq('contact_id', id)
        .order('scheduled_date', { ascending: false, nullsFirst: false })
        .limit(jobsLimit + 1)
      if (jobsFilter === 'on_hold') {
        q = q.in('status', HOLD_STATUSES)
      } else if (jobsFilter !== 'all') {
        q = q.eq('status', jobsFilter)
      }
      const { data } = await q
      return data || []
    },
    enabled: !!id,
  })
  const visibleJobs = jobs.slice(0, jobsLimit)
  const hasMoreJobs = jobs.length > jobsLimit

  const { data: quotes = [] } = useQuery({
    queryKey: ['contact-quotes', id, quotesFilter, quotesLimit],
    queryFn: async () => {
      let q = supabase
        .from('quotes')
        .select('id, quote_number, status, total, sent_at, valid_until, created_at')
        .eq('contact_id', id)
        .order('created_at', { ascending: false })
        .limit(quotesLimit + 1)
      if (quotesFilter !== 'all') q = q.eq('status', quotesFilter)
      const { data } = await q
      return data || []
    },
    enabled: !!id,
  })
  const visibleQuotes = quotes.slice(0, quotesLimit)
  const hasMoreQuotes = quotes.length > quotesLimit

  const { data: notesCount = 0 } = useQuery({
    queryKey: ['contact-notes-count', id],
    queryFn: async () => {
      const { count } = await supabase
        .from('entity_notes')
        .select('id', { count: 'exact', head: true })
        .eq('entity_type', 'contact')
        .eq('entity_id', id!)
      return count || 0
    },
    enabled: !!id,
  })

  const { data: jobsCount = 0 } = useQuery({
    queryKey: ['contact-jobs-count', id],
    queryFn: async () => {
      const { count } = await supabase
        .from('field_jobs')
        .select('id', { count: 'exact', head: true })
        .eq('contact_id', id!)
      return count || 0
    },
    enabled: !!id,
  })

  const { data: quotesCount = 0 } = useQuery({
    queryKey: ['contact-quotes-count', id],
    queryFn: async () => {
      const { count } = await supabase
        .from('quotes')
        .select('id', { count: 'exact', head: true })
        .eq('contact_id', id!)
      return count || 0
    },
    enabled: !!id,
  })

  const companyId = (contact as any)?.company_id ?? null
  const { data: companyLocations = [] } = useQuery({
    queryKey: ['contact-company-locations', companyId],
    queryFn: async () => {
      const { data } = await supabase
        .from('locations')
        .select('id, name, city, state')
        .eq('company_id', companyId)
        .order('name', { ascending: true })
      return data || []
    },
    enabled: !!companyId,
  })

  const updateMutation = useMutation({
    mutationFn: async (updates: Record<string, any>) => {
      const { error } = await supabase.from('contacts').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['contact', id] }); queryClient.invalidateQueries({ queryKey: ['contacts'] }); toast.success('Saved') },
    onError: () => toast.error('Failed to save'),
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('contacts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['contacts'] }); navigate('/app/contacts'); toast.success('Contact deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  if (isLoading) return (
    <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (!contact) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)' }}>Contact not found</div>

  const name = `${contact.first_name} ${contact.last_name || ''}`.trim()
  const sc = getStatusColor(contact.status)
  const TABS = [
    { id: 'timeline', label: 'Activity', count: 0 },
    { id: 'jobs', label: 'Jobs', count: jobsCount },
    { id: 'quotes', label: 'Quotes', count: quotesCount },
    { id: 'deals', label: 'Deals', count: deals.length },
    { id: 'invoices', label: 'Invoices', count: invoices.length },
    { id: 'billing', label: 'Billing', count: 0 },
    { id: 'activity', label: 'Conversations', count: conversations.length },
    { id: 'notes', label: 'Notes', count: notesCount },
    { id: 'tasks', label: 'Tasks', count: 0 },
    { id: 'meetings', label: 'Meetings', count: 0 },
  ]
  const JOB_STATUSES: Array<{ id: typeof jobsFilter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'en_route', label: 'En route' },
    { id: 'on_site', label: 'On site' },
    { id: 'on_hold', label: 'On hold' },
    { id: 'complete', label: 'Complete' },
    { id: 'cancelled', label: 'Cancelled' },
  ]
  const QUOTE_STATUSES: Array<{ id: typeof quotesFilter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'draft', label: 'Draft' },
    { id: 'sent', label: 'Sent' },
    { id: 'approved', label: 'Approved' },
    { id: 'declined', label: 'Declined' },
    { id: 'expired', label: 'Expired' },
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '16px 28px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/contacts') }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-secondary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <span style={{ fontSize: 13, color: 'var(--ink-secondary)', fontWeight: 500 }}>{name}</span>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => { if (window.confirm(`Delete ${name}?`)) deleteMutation.mutate() }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--danger) var(--status-tint-fill), transparent)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <Trash2 style={{ width: 12, height: 12 }} /> Delete
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: 280, borderRight: '1px solid var(--border-subtle)', padding: '24px 20px', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ margin: '0 auto 12px', width: 'fit-content' }}><Avatar name={name || 'U'} size={56} /></div>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--ink-primary)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>{name}</h2>
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 100, background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, textTransform: 'capitalize' }}>{contact.status}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
            {[
              { icon: MessageSquare, label: 'Message', color: 'var(--accent)', action: () => navigate('/app/inbox') },
              { icon: Mail, label: 'Email', color: 'var(--info)', action: () => contact.email && window.open(`mailto:${contact.email}`) },
              { icon: Phone, label: 'Call', color: 'var(--positive)', action: () => contact.phone && window.open(`tel:${contact.phone}`) },
              { icon: FileText, label: 'Invoice', color: 'var(--warning)', action: () => navigate('/app/invoices') },
            ].map(a => (
              <button key={a.label} onClick={a.action}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: '10px 8px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, cursor: 'pointer', transition: 'all 150ms ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = `color-mix(in srgb, ${a.color} var(--status-tint-faint), transparent)`; e.currentTarget.style.borderColor = `color-mix(in srgb, ${a.color} var(--status-tint-strong), transparent)` }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                <a.icon style={{ width: 16, height: 16, color: a.color }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-secondary)' }}>{a.label}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <Building2 style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company</span>
              </div>

              {linking ? (
                <div>
                  <CompanyPicker
                    workspaceId={workspace?.id || ''}
                    value={{ id: contact.company_id || null, name: contact.company_name || '' }}
                    onChange={next => {
                      updateMutation.mutate(
                        { company_id: next.id, company_name: next.name },
                        { onSuccess: () => setLinking(false) },
                      )
                    }}
                    placeholder="Search or create company…"
                  />
                  <button type="button" onClick={() => setLinking(false)}
                    style={{ marginTop: 8, padding: '4px 8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 11 }}>
                    Cancel
                  </button>
                </div>
              ) : contact.company_id ? (
                <button type="button" onClick={() => navigate(`/app/companies/${contact.company_id}`)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 9, cursor: 'pointer', textAlign: 'left', transition: 'all 120ms' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Building2 style={{ width: 13, height: 13, color: 'var(--accent-text)' }} />
                  </div>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.company_name || 'Linked company'}</span>
                  <ChevronRight style={{ width: 13, height: 13, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                </button>
              ) : contact.company_name ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--hover-bg)', border: '1px dashed var(--border-default)', borderRadius: 9 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contact.company_name}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 100, background: 'var(--warning-dim)', color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>Unlinked</span>
                  </div>
                  <button type="button" onClick={() => setLinking(true)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px 10px', background: 'none', border: '1px solid var(--border-default)', borderRadius: 8, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600 }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Link2 style={{ width: 12, height: 12 }} /> Link to company
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setLinking(true)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 12px', background: 'var(--hover-bg)', border: '1px dashed var(--border-default)', borderRadius: 9, cursor: 'pointer', color: 'var(--ink-tertiary)', fontSize: 13, fontWeight: 500 }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent-text)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-tertiary)'; e.currentTarget.style.borderColor = 'var(--border-default)' }}>
                  <Building2 style={{ width: 13, height: 13 }} /> Attach to company
                </button>
              )}
            </div>

            {contact.company_id && companyLocations.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                  <MapPin style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company locations</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {companyLocations.map((loc: any) => (
                    <button key={loc.id} type="button"
                      onClick={() => navigate(`/app/companies/${contact.company_id}?tab=locations`)}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, padding: '7px 10px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 8, cursor: 'pointer', textAlign: 'left', transition: 'background 120ms' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{loc.name || 'Location'}</span>
                      {(loc.city || loc.state) && (
                        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
                          {[loc.city, loc.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {[
              { label: 'Email', icon: Mail, field: 'email', type: 'email', value: contact.email },
              { label: 'Phone', icon: Phone, field: 'phone', value: contact.phone },
              { label: 'Address line 1', icon: MapPin, field: 'address_line1', value: contact.address_line1 },
              { label: 'Address line 2', icon: MapPin, field: 'address_line2', value: contact.address_line2 },
              { label: 'City', icon: MapPin, field: 'address_city', value: contact.address_city },
              { label: 'State', icon: MapPin, field: 'address_state', value: contact.address_state },
              { label: 'ZIP', icon: MapPin, field: 'address_zip', value: contact.address_zip },
              { label: 'Country', icon: MapPin, field: 'address_country', value: contact.address_country },
            ].map(f => (
              <div key={f.field}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                  <f.icon style={{ width: 11, height: 11, color: 'var(--ink-tertiary)' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{f.label}</span>
                </div>
                <EditableField label={f.label} value={f.value} type={f.type} placeholder={`Add ${f.label.toLowerCase()}...`} onSave={(val: string) => updateMutation.mutate({ [f.field]: val })} />
              </div>
            ))}

            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Status</span>
              <select value={contact.status} onChange={e => updateMutation.mutate({ status: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="active">Active</option>
                <option value="lead">Lead</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Lifecycle — the sales-journey dimension (distinct from Status). */}
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Lifecycle</span>
              <select value={contact.lifecycle_stage || 'lead'} onChange={e => updateMutation.mutate({ lifecycle_stage: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="lead">Lead</option>
                <option value="prospect">Prospect</option>
                <option value="customer">Customer</option>
                <option value="churned">Churned</option>
              </select>
            </div>

            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Tags</span>
              <TagEditor value={contact.tags} onChange={next => updateMutation.mutate({ tags: next })} />
            </div>

            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Custom fields</span>
              <CustomFieldsEditor value={contact.custom_fields} onChange={next => updateMutation.mutate({ custom_fields: next })} />
            </div>

            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Quick summary</span>
              <textarea defaultValue={contact.notes || ''} onBlur={e => updateMutation.mutate({ notes: e.target.value })} placeholder="A one-line summary..." rows={3}
                style={{ width: '100%', padding: '8px 10px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'} />
            </div>

            {isOwnerOrAdmin && id && (
              <div style={{ paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
                <PortalAccessSection contactId={id} contactEmail={contact.email} />
              </div>
            )}

            <div style={{ paddingTop: 8, borderTop: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '0 0 4px' }}>Created {formatDate(contact.created_at)}</p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>Updated {formatRelativeDate(contact.updated_at)}</p>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '0 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexShrink: 0, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ padding: '14px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500, color: activeTab === tab.id ? 'var(--ink-primary)' : 'var(--ink-secondary)', borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all 150ms ease', display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {tab.label}
                {tab.count > 0 && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 100, background: activeTab === tab.id ? 'var(--accent-dim)' : 'var(--border-subtle)', color: activeTab === tab.id ? 'var(--accent-text)' : 'var(--ink-tertiary)' }}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
            {activeTab === 'activity' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {conversations.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <MessageSquare style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No conversations yet</p>
                  </div>
                ) : conversations.map((conv: any) => {
                  const s = getStatusColor(conv.status)
                  return (
                    <div key={conv.id} onClick={() => navigate(`/app/inbox/${conv.id}`)}
                      style={{ display: 'flex', gap: 12, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, cursor: 'pointer', transition: 'all 150ms ease' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border-subtle)' }}>
                      <MessageSquare style={{ width: 16, height: 16, color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{conv.subject || 'Conversation'}</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: s.bg, color: s.text, textTransform: 'capitalize' }}>{conv.status}</span>
                            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(conv.created_at)}</span>
                          </div>
                        </div>
                        {conv.last_message && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conv.last_message}</p>}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {activeTab === 'timeline' && id && (
              <ActivityTimeline entityType="contact" entityId={id} />
            )}

            {activeTab === 'jobs' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                  {JOB_STATUSES.map(s => (
                    <button key={s.id} onClick={() => { setJobsFilter(s.id); setJobsLimit(25) }}
                      style={{ padding: '5px 10px', borderRadius: 100, border: '1px solid', borderColor: jobsFilter === s.id ? 'var(--accent)' : 'var(--border-default)', background: jobsFilter === s.id ? 'var(--accent-dim)' : 'transparent', color: jobsFilter === s.id ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      {s.label}
                    </button>
                  ))}
                </div>

                {visibleJobs.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Wrench style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No jobs{jobsFilter !== 'all' ? ` in ${jobsFilter.replace(/_/g, ' ')}` : ' yet'}</p>
                  </div>
                ) : visibleJobs.map((job: any) => {
                  const s = getStatusColor(job.status)
                  const addr = formatAddress(fromAddressPrefix(job))
                  return (
                    <div key={job.id} onClick={() => navigate(`/app/jobs/${job.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                      <Wrench style={{ width: 16, height: 16, color: 'var(--accent)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{job.job_number || 'Job'}</span>
                          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{job.scheduled_date ? formatDate(job.scheduled_date) : 'Unscheduled'}</span>
                        </div>
                        {addr && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{addr}</p>}
                        {job.assigned_agent_name && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>Assigned to {job.assigned_agent_name}</p>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {typeof job.total === 'number' && job.total > 0 && <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{formatCurrency(job.total)}</p>}
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: s.bg, color: s.text, textTransform: 'capitalize' }}>{(job.status || '').replace('_', ' ')}</span>
                      </div>
                    </div>
                  )
                })}

                {hasMoreJobs && (
                  <button onClick={() => setJobsLimit(n => n + 25)}
                    style={{ padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', cursor: 'pointer', alignSelf: 'center', marginTop: 4 }}>
                    Load more
                  </button>
                )}
              </div>
            )}

            {activeTab === 'quotes' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                  {QUOTE_STATUSES.map(s => (
                    <button key={s.id} onClick={() => { setQuotesFilter(s.id); setQuotesLimit(25) }}
                      style={{ padding: '5px 10px', borderRadius: 100, border: '1px solid', borderColor: quotesFilter === s.id ? 'var(--accent)' : 'var(--border-default)', background: quotesFilter === s.id ? 'var(--accent-dim)' : 'transparent', color: quotesFilter === s.id ? 'var(--accent-text)' : 'var(--ink-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      {s.label}
                    </button>
                  ))}
                </div>

                {visibleQuotes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <FileText style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No quotes{quotesFilter !== 'all' ? ` in ${quotesFilter}` : ' yet'}</p>
                  </div>
                ) : visibleQuotes.map((quote: any) => {
                  const cfg = getQuoteStatusConfig(quote.status)
                  return (
                    <div key={quote.id} onClick={() => navigate(`/app/quotes/${quote.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--surface)'}>
                      <FileText style={{ width: 16, height: 16, color: 'var(--accent)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px', fontFamily: 'monospace' }}>{quote.quote_number}</p>
                        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>
                          {quote.sent_at ? `Sent ${formatRelativeDate(quote.sent_at)}` : 'Not sent'}
                          {quote.valid_until && ` · Expires ${formatDate(quote.valid_until)}`}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {typeof quote.total === 'number' && <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{formatCurrency(quote.total)}</p>}
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                      </div>
                    </div>
                  )
                })}

                {hasMoreQuotes && (
                  <button onClick={() => setQuotesLimit(n => n + 25)}
                    style={{ padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', cursor: 'pointer', alignSelf: 'center', marginTop: 4 }}>
                    Load more
                  </button>
                )}
              </div>
            )}

            {activeTab === 'deals' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {deals.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <TrendingUp style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No deals yet</p>
                  </div>
                ) : deals.map((deal: any) => {
                  const s = getStatusColor(deal.status)
                  return (
                    <div key={deal.id} onClick={() => navigate('/app/deals')}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, cursor: 'pointer' }}>
                      <TrendingUp style={{ width: 16, height: 16, color: 'var(--warning)', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{deal.title}</p>
                        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{deal.stage_name || 'No stage'}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {deal.value && <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--positive)', margin: '0 0 2px' }}>${deal.value.toLocaleString()}</p>}
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: s.bg, color: s.text, textTransform: 'capitalize' }}>{deal.status}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {activeTab === 'notes' && id && (
              <NoteThread entityType="contact" entityId={id} />
            )}

            {activeTab === 'tasks' && id && (
              <TasksThread entityType="contact" entityId={id} />
            )}

            {activeTab === 'meetings' && id && (
              <MeetingsThread entityType="contact" entityId={id} />
            )}

            {activeTab === 'billing' && id && (
              <BillingTab contactIds={[id]} />
            )}

            {activeTab === 'invoices' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {invoices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <FileText style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', margin: '0 auto 10px', display: 'block' }} />
                    <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>No invoices yet</p>
                  </div>
                ) : invoices.map((inv: any) => {
                  const s = getStatusColor(inv.status)
                  return (
                    <div key={inv.id} onClick={() => navigate(`/app/invoices/${inv.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, cursor: 'pointer' }}>
                      <FileText style={{ width: 16, height: 16, color: 'var(--positive)', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 2px' }}>{inv.invoice_number}</p>
                        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{formatDate(inv.issue_date)}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 2px' }}>${inv.total?.toLocaleString()}</p>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 100, background: s.bg, color: s.text, textTransform: 'capitalize' }}>{inv.status}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Portal access section ──────────────────────────────────────────────
// Visible only to owners/admins (gated at the consumer). Shows the
// current state — no-access / invited / active with last login — and
// the "Invite to portal" button when the contact has an email and no
// portal_users row yet. Hitting the button calls the portal-invite
// edge function; on success, refetches the local portal_users row so
// the state flips without a page reload.
function PortalAccessSection({ contactId, contactEmail }: { contactId: string; contactEmail: string | null }) {
  const queryClient = useQueryClient()
  const { data: portalUser, isLoading } = useQuery({
    queryKey: ['portal-user-for-contact', contactId],
    queryFn: async () => {
      const { data } = await supabase
        .from('portal_users')
        .select('id, last_login_at, created_at, role')
        .eq('contact_id', contactId)
        .maybeSingle()
      return data
    },
  })

  // First-admin detection: count existing portal_users at this contact's
  // company. 0 → propose 'admin' as default; otherwise 'member'. The
  // function-side does the same auto-detect when role is omitted, but
  // surfacing the default in the picker lets the operator override
  // before sending.
  const { data: companyFirstAdmin } = useQuery({
    queryKey: ['portal-first-admin-detect', contactId],
    queryFn: async () => {
      const { data: c } = await supabase.from('contacts').select('company_id').eq('id', contactId).maybeSingle()
      if (!c?.company_id) return 'admin' as const  // contacts with no company can still get portal access — default to admin since they're solo
      const { count } = await supabase.from('portal_users').select('id', { count: 'exact', head: true }).eq('company_id', c.company_id)
      return (count ?? 0) === 0 ? ('admin' as const) : ('member' as const)
    },
    enabled: !portalUser, // only need this in the pre-invite state
  })

  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member')
  const [roleUserPicked, setRoleUserPicked] = useState(false)
  // Sync the picker default once the detect query resolves — unless
  // the operator has already manually picked something, in which case
  // we respect their choice.
  useEffect(() => {
    if (!roleUserPicked && companyFirstAdmin === 'admin') setRole('admin')
  }, [companyFirstAdmin, roleUserPicked])

  const [busy, setBusy] = useState(false)
  const [revoking, setRevoking] = useState(false)

  // Shared error extractor — supabase.functions.invoke returns non-2xx as
  // an error without auto-pulling the body. Same idiom both branches use.
  const extractError = async (error: unknown, fallback: string): Promise<string> => {
    const ctx = (error as { context?: { text?: () => Promise<string> } }).context
    if (ctx && typeof ctx.text === 'function') {
      try {
        const text = await ctx.text()
        const parsed = JSON.parse(text)
        return parsed?.error ?? fallback
      } catch { /* fall through */ }
    }
    return (error as Error).message || fallback
  }

  const handleInvite = async () => {
    if (busy) return
    setBusy(true)
    const { data, error } = await supabase.functions.invoke<{ ok: boolean; state: string; warning?: string }>(
      'portal-invite',
      { body: { contact_id: contactId, action: 'invite', role } },
    )
    setBusy(false)
    if (error) {
      toast.error(await extractError(error, 'Invite failed'))
      return
    }
    if (data?.warning) {
      toast.warning(`Invite created — ${data.warning}`)
    } else {
      toast.success(`Portal invite sent (${role})`)
    }
    queryClient.invalidateQueries({ queryKey: ['portal-user-for-contact', contactId] })
  }

  const handleRevoke = async () => {
    if (revoking) return
    if (!confirm('Revoke portal access for this contact? They will be signed out and lose access immediately. Records they responded to (quotes etc.) are preserved with the responder name removed.')) return
    setRevoking(true)
    const { data, error } = await supabase.functions.invoke<{ ok: boolean; state: string; warning?: string }>(
      'portal-invite',
      { body: { contact_id: contactId, action: 'revoke' } },
    )
    setRevoking(false)
    if (error) {
      toast.error(await extractError(error, 'Revoke failed'))
      return
    }
    if (data?.warning) {
      toast.warning(`Portal access revoked — ${data.warning}`)
    } else {
      toast.success('Portal access revoked')
    }
    queryClient.invalidateQueries({ queryKey: ['portal-user-for-contact', contactId] })
  }

  return (
    <>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Portal access</span>
      {isLoading ? (
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Checking…</p>
      ) : portalUser ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            {portalUser.last_login_at ? (
              <ShieldCheck style={{ width: 14, height: 14, color: 'var(--positive)', flexShrink: 0, marginTop: 2 }} />
            ) : (
              <Clock style={{ width: 14, height: 14, color: 'var(--warning)', flexShrink: 0, marginTop: 2 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>
                {portalUser.last_login_at ? 'Active' : 'Invited'}
              </p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '2px 0 0' }}>
                {portalUser.last_login_at
                  ? `Last login ${formatRelativeDate(portalUser.last_login_at)}`
                  : `Invite sent ${formatRelativeDate(portalUser.created_at)}`}
              </p>
            </div>
          </div>
          {/* Revoke is destructive — danger token, paired with a confirm
              dialog in the handler. Stays inside the owner/admin gate
              that wraps this section. */}
          <button type="button" onClick={handleRevoke} disabled={revoking}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              alignSelf: 'flex-start',
              padding: '5px 10px', background: 'transparent',
              border: '1px solid var(--danger-border)', borderRadius: 7,
              color: 'var(--danger)', fontSize: 11, fontWeight: 600,
              cursor: revoking ? 'wait' : 'pointer', opacity: revoking ? 0.7 : 1,
            }}>
            {revoking ? <Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> : <ShieldOff style={{ width: 11, height: 11 }} />}
            {revoking ? 'Revoking…' : 'Revoke access'}
          </button>
        </div>
      ) : !contactEmail ? (
        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Add an email to enable portal access.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <select value={role} onChange={e => { setRoleUserPicked(true); setRole(e.target.value as 'admin' | 'member' | 'viewer') }}
            style={{ padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 7, fontSize: 11, fontWeight: 600, color: 'var(--ink-primary)', cursor: 'pointer' }}>
            <option value="admin">Admin — full access</option>
            <option value="member">Member — approve under threshold</option>
            <option value="viewer">Viewer — operational read only</option>
          </select>
          {companyFirstAdmin === 'admin' && !roleUserPicked && (
            <p style={{ fontSize: 10, color: 'var(--ink-tertiary)', margin: 0 }}>
              First portal user at this company — defaulting to Admin.
            </p>
          )}
          <button type="button" onClick={handleInvite} disabled={busy}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              alignSelf: 'flex-start',
              padding: '7px 12px', background: 'var(--accent-dim)',
              border: '1px solid var(--accent-border)', borderRadius: 8,
              color: 'var(--accent-text)', fontSize: 12, fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
            }}>
            {busy ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 12, height: 12 }} />}
            {busy ? 'Sending…' : 'Invite to portal'}
          </button>
        </div>
      )}
    </>
  )
}